import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  BarChart3, Download, FileText, Users, Package, TrendingUp,
  Calendar, RefreshCw, FileDown, Clock, CheckCircle, Building2,
  Heart, AlertTriangle, PieChart
} from 'lucide-react';
import { toast } from 'sonner';

export default function PortalRelatoriosPage() {
  const [loading, setLoading] = useState(true);
  const [instituicao, setInstituicao] = useState(null);
  const [stats, setStats] = useState({
    utentes: 0,
    valencias: 0,
    pedidos_ano: 0,
    documentos: 0,
    voluntarios: 0
  });
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState('');
  const [selectedPeriodo, setSelectedPeriodo] = useState('anual');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const userStr = localStorage.getItem('portal_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const instituicaoId = user.instituicao_id;

      // Load institution and dashboard data
      const [instRes, dashRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instituicoes/${instituicaoId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (instRes.ok) {
        const instData = await instRes.json();
        setInstituicao(instData);
        
        setStats(prev => ({
          ...prev,
          utentes: instData.total_utentes || 0,
          valencias: instData.valencias?.length || 0
        }));
      }

      if (dashRes.ok) {
        const dashData = await dashRes.json();
        setStats(prev => ({
          ...prev,
          pedidos_ano: (dashData.cards?.pedidos?.pendentes || 0) + 
                       (dashData.cards?.pedidos?.aprovados_aguardando || 0),
          documentos: dashData.cards?.documentos?.validos || 0,
          voluntarios: dashData.cards?.voluntarios?.ativos || 0
        }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (reportType) => {
    setGenerating(true);
    setSelectedReport(reportType);
    
    try {
      const token = localStorage.getItem('portal_token');
      const userStr = localStorage.getItem('portal_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const instituicaoId = user.instituicao_id;
      
      let endpoint = '';
      let filename = '';
      
      switch (reportType) {
        case 'ficha':
          endpoint = `/api/instituicoes/${instituicaoId}/pdf`;
          filename = `ficha_instituicao_${instituicaoId}.pdf`;
          break;
        case 'utentes':
          endpoint = `/api/instituicoes/${instituicaoId}/utentes/pdf`;
          filename = `relatorio_utentes_${instituicaoId}.pdf`;
          break;
        case 'pedidos':
          endpoint = `/api/portal/pedidos/relatorio/pdf?periodo=${selectedPeriodo}`;
          filename = `relatorio_pedidos_${selectedPeriodo}.pdf`;
          break;
        case 'atividades':
          endpoint = `/api/instituicoes/${instituicaoId}/atividades/pdf`;
          filename = `relatorio_atividades_${instituicaoId}.pdf`;
          break;
        default:
          toast.error('Tipo de relatório não suportado');
          setGenerating(false);
          return;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Relatório gerado com sucesso');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Erro ao gerar relatório');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setGenerating(false);
      setSelectedReport('');
    }
  };

  const REPORTS = [
    {
      id: 'ficha',
      title: 'Ficha da Instituição',
      description: 'Dados gerais, contactos, respostas sociais e informações legais',
      icon: Building2,
      color: 'bg-blue-500'
    },
    {
      id: 'utentes',
      title: 'Relatório de Utentes',
      description: 'Listagem de utentes por resposta social com dados demográficos',
      icon: Users,
      color: 'bg-green-500'
    },
    {
      id: 'pedidos',
      title: 'Relatório de Pedidos',
      description: 'Histórico de pedidos de recursos e seu estado',
      icon: Package,
      color: 'bg-amber-500'
    },
    {
      id: 'atividades',
      title: 'Relatório de Atividades',
      description: 'Resumo das atividades e eventos realizados',
      icon: Calendar,
      color: 'bg-purple-500'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="portal-relatorios-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-relatorios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#0066CC]" />
            Relatórios
          </h1>
          <p className="text-slate-500">Gerar e descarregar relatórios da instituição</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar Dados
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.valencias}</p>
                <p className="text-xs text-slate-500">Respostas Sociais</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.utentes}</p>
                <p className="text-xs text-slate-500">Utentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.pedidos_ano}</p>
                <p className="text-xs text-slate-500">Pedidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.documentos}</p>
                <p className="text-xs text-slate-500">Documentos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.voluntarios}</p>
                <p className="text-xs text-slate-500">Voluntários</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Período para relatórios:
            </span>
            <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mensal">Último Mês</SelectItem>
                <SelectItem value="trimestral">Último Trimestre</SelectItem>
                <SelectItem value="semestral">Último Semestre</SelectItem>
                <SelectItem value="anual">Último Ano</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reports Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {REPORTS.map((report) => {
          const Icon = report.icon;
          const isGenerating = generating && selectedReport === report.id;
          
          return (
            <Card key={report.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${report.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{report.title}</CardTitle>
                      <CardDescription>{report.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => handleGenerateReport(report.id)}
                  disabled={generating}
                  className="w-full bg-[#0066CC] hover:bg-[#0055AA]"
                  data-testid={`generate-${report.id}-btn`}
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      A gerar...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Gerar PDF
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-300">Nota sobre Relatórios</h4>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                Os relatórios são gerados com os dados mais recentes disponíveis no sistema. 
                Certifique-se de que toda a informação está atualizada antes de gerar relatórios oficiais.
                Os ficheiros PDF podem ser utilizados para entrega a entidades oficiais.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Relatórios Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <PieChart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>O histórico de relatórios gerados será exibido aqui</p>
            <p className="text-sm mt-1">Funcionalidade em desenvolvimento</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
